import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Eye,
  EyeOff,
  Loader2,
  Mail,
  Lock,
  User,
  ArrowRight,
  ShieldCheck,
  Check,
} from "lucide-react";
import { useAuthStore } from "../store/useAuthStore";

const SignUpPage = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
  });
  const { signup, isSigningUp } = useAuthStore();

  const strength = (() => {
    const p = formData.password;
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    return score;
  })();
  const strengthLabels = ["", "Weak", "Fair", "Good", "Strong"];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    signup(formData);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-white">

      <div className="relative flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-14">
        <Link
          to="/"
          className="absolute top-8 left-8 lg:left-20 flex items-center gap-2.5 group"
        >
          <BrandMark />
          <div className="leading-tight">
            <p className="text-sm font-bold text-slate-800 tracking-tight">
              CRE
            </p>
            <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-[0.18em]">
              by DymaxTech
            </p>
          </div>
        </Link>

        <div className="max-w-md w-full mx-auto">
          <h1 className="text-3xl sm:text-[34px] font-extrabold text-slate-800 tracking-tight leading-[1.1]">
            Create your account.
          </h1>
          <p className="text-[15px] text-slate-500 mt-2.5 leading-relaxed">
            Set up your CRE workspace in under a minute — no credit card needed.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-4">

            <div>
              <label className="block text-[11px] font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                Full name
              </label>
              <div className="relative">
                <User
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="text"
                  required
                  autoComplete="name"
                  className="w-full pl-10 pr-4 py-3 text-sm border border-slate-200 rounded-xl bg-slate-50/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/30 focus:border-[#00AAF1]/50 transition placeholder:text-slate-400"
                  placeholder="Abdullah Rehman"
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData({ ...formData, fullName: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                Email address
              </label>
              <div className="relative">
                <Mail
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type="email"
                  required
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-3 text-sm border border-slate-200 rounded-xl bg-slate-50/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/30 focus:border-[#00AAF1]/50 transition placeholder:text-slate-400"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-700 mb-1.5 uppercase tracking-wider">
                Password
              </label>
              <div className="relative">
                <Lock
                  size={16}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
                />
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  minLength={8}
                  className="w-full pl-10 pr-10 py-3 text-sm border border-slate-200 rounded-xl bg-slate-50/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#00AAF1]/30 focus:border-[#00AAF1]/50 transition placeholder:text-slate-400"
                  placeholder="At least 8 characters"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700 transition"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {formData.password && (
                <div className="mt-2.5">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                          i <= strength ? "bg-[#03488A]" : "bg-slate-100"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1.5 font-medium">
                    Password strength:{" "}
                    <span className="font-bold text-slate-700">
                      {strengthLabels[strength] || "Too short"}
                    </span>
                  </p>
                </div>
              )}
            </div>

            <p className="text-[11px] text-slate-500 leading-relaxed">
              By creating an account, you agree to our{" "}
              <Link
                to="#"
                className="text-[#03488A] font-semibold hover:text-[#00AAF1]"
              >
                Terms
              </Link>{" "}
              and{" "}
              <Link
                to="#"
                className="text-[#03488A] font-semibold hover:text-[#00AAF1]"
              >
                Privacy Policy
              </Link>
              .
            </p>

            <button
              type="submit"
              disabled={isSigningUp}
              className="w-full mt-2 py-3 rounded-xl bg-[#03488A] hover:bg-[#024078] text-white font-semibold text-sm shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 group"
            >
              {isSigningUp ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Creating your workspace…
                </>
              ) : (
                <>
                  Create account
                  <ArrowRight
                    size={15}
                    className="group-hover:translate-x-0.5 transition"
                  />
                </>
              )}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-[0.2em]">
              or
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <p className="text-center text-sm text-slate-600">
            Already have an account?{" "}
            <Link
              to="/login"
              className="font-semibold text-[#03488A] hover:text-[#00AAF1] transition"
            >
              Sign in
            </Link>
          </p>
        </div>

        <div className="absolute bottom-6 left-8 lg:left-20 right-8 flex items-center gap-3 text-[11px] text-slate-400">
          <ShieldCheck size={12} className="text-slate-400" />
          <span>Secure, encrypted connection</span>
          <span className="text-slate-200">·</span>
          <Link to="#" className="hover:text-slate-600 transition">
            Privacy
          </Link>
          <span className="text-slate-200">·</span>
          <Link to="#" className="hover:text-slate-600 transition">
            Terms
          </Link>
        </div>
      </div>

      <div className="hidden lg:flex relative overflow-hidden text-white bg-[radial-gradient(ellipse_at_top_right,#0e2a50_0%,#0a1f3d_55%,#061729_100%)]">
        <div
          aria-hidden
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "radial-gradient(700px 400px at 80% 0%, rgba(0,170,241,0.18), transparent 65%)",
          }}
        />
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "radial-gradient(circle at center, white 1px, transparent 1px)",
            backgroundSize: "32px 32px",
          }}
        />

        <div className="relative flex flex-col justify-between p-10 xl:p-14 w-full max-w-xl mx-auto">
          <div>
            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-[0.2em] mb-6">
              Client Relationship Ecosystem
            </p>
            <h2 className="text-4xl xl:text-5xl font-extrabold tracking-tight leading-[1.05] text-white">
              Bring your team
              <br />
              and your clients
              <br />
              <span className="text-[#7ccfff]">closer together.</span>
            </h2>
            <p className="text-[15px] text-slate-300/75 mt-5 max-w-md leading-relaxed">
              CRE gives business developers and clients a shared space for
              proposals, conversations, and projects — no more scattered
              threads or lost context.
            </p>
          </div>

          <ul className="space-y-4 my-10">
            <Benefit>
              <strong className="text-white font-semibold">
                One shared pipeline.
              </strong>{" "}
              BDs track leads, clients track progress — same view.
            </Benefit>
            <Benefit>
              <strong className="text-white font-semibold">
                Unified communication.
              </strong>{" "}
              Messages, proposals, and questionnaires in one thread.
            </Benefit>
            <Benefit>
              <strong className="text-white font-semibold">
                Free while in beta.
              </strong>{" "}
              Full access, no credit card, no setup fees.
            </Benefit>
          </ul>

          <div>
            <div className="grid grid-cols-3 gap-4 pb-5 border-b border-white/10">
              <Stat value="120+" label="Teams" />
              <Stat value="2,500+" label="Projects" />
              <Stat value="4.9★" label="Rating" />
            </div>
            <p className="text-[11px] text-slate-400/80 mt-4">
              Trusted by client-facing teams worldwide.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const BrandMark = () => (
  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00AAF1] to-[#03488A] grid place-items-center shadow-md shadow-[#03488A]/20 group-hover:scale-105 transition">
    <svg
      viewBox="0 0 24 24"
      fill="none"
      className="w-5 h-5"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="9"
        cy="12"
        r="5.5"
        stroke="white"
        strokeWidth="2"
        opacity="0.6"
      />
      <circle cx="15" cy="12" r="5.5" stroke="white" strokeWidth="2" />
    </svg>
  </div>
);

const Benefit = ({ children }: { children: React.ReactNode }) => (
  <li className="flex items-start gap-3">
    <div className="w-5 h-5 rounded-md bg-white/10 ring-1 ring-white/15 grid place-items-center mt-0.5 flex-shrink-0">
      <Check size={12} className="text-white" strokeWidth={3} />
    </div>
    <p className="text-[14px] text-slate-300/85 leading-relaxed">{children}</p>
  </li>
);

const Stat = ({ value, label }: { value: string; label: string }) => (
  <div>
    <p className="text-2xl xl:text-[28px] font-extrabold text-white">{value}</p>
    <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mt-1">
      {label}
    </p>
  </div>
);

export default SignUpPage;
